/**
 * Copyright (C) 2023 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */

import { Button } from '@mui/material';
import { FormikValues } from 'formik';
import { isNil } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { ServiceTierRequestStatus } from '../../../../shared/entities/serviceTierRequest';
import {
  selectCurrentServiceTierChangeRequest,
  selectRequestingPostServiceTierRequestError,
  selectRequestingPostServiceTierRequestSuccess,
} from '../../../../store';
import {
  clearReceivedPostServiceTierRequestStatus,
  deleteServiceTierChangeRequest,
  getCurrentServiceTierChangeRequest,
} from '../../../../store/serviceTierRequests/actions';
import ModalDialog from '../../../common/dialogs/ModalDialog';
import ServiceTierChangeRequestDialog from '../../../provisioning/ServiceTierChangeRequestModal/ServiceTierChangeRequestDialog';
import ServiceTierChangeRequestErrorDialog from '../../../provisioning/ServiceTierChangeRequestModal/ServiceTierChangeRequestErrorDialog';
import ServiceTierChangeRequestSuccessDialog from '../../../provisioning/ServiceTierChangeRequestModal/ServiceTierChangeRequestSuccessDialog';

export interface ServiceTierChangeRequestButtonProps {
  terminalDisplayName: string | undefined | null;
  terminalId: string | undefined | null;
  contractNumber: string | undefined | null;
}

const ServiceTierChangeRequestButton = ({
  terminalDisplayName,
  contractNumber,
  terminalId,
}: ServiceTierChangeRequestButtonProps) => {
  // Local State
  const [cancelServiceTierChangeRequestDialogOpen, setCancelServiceTierChangeRequestDialogOpen] =
    useState<boolean>(false);
  const [serviceTierChangeRequestDialogOpen, setServiceTierChangeRequestDialogOpen] = useState<boolean>(false);

  // Selectors
  const currentServiceTierChangeRequest = useSelector(selectCurrentServiceTierChangeRequest, shallowEqual);
  const postServiceTierRequestSuccess = useSelector(selectRequestingPostServiceTierRequestSuccess, shallowEqual);
  const postServiceTierRequestError = useSelector(selectRequestingPostServiceTierRequestError, shallowEqual);

  // 3rd Party Hooks
  const dispatch = useDispatch();
  const formRef = useRef<FormikValues>();

  // Lifecycle hooks
  useEffect(() => {
    if (terminalDisplayName) {
      dispatch(getCurrentServiceTierChangeRequest(terminalDisplayName));
    }
  }, [dispatch, terminalDisplayName]);

  // If there is not an existing service tier request or if there is one and it is in progress or submitted, show the cancel button
  // else if there is one and it is completed or denied show the request button
  return isNil(currentServiceTierChangeRequest) ||
    currentServiceTierChangeRequest?.status === ServiceTierRequestStatus.COMPLETED ||
    currentServiceTierChangeRequest?.status === ServiceTierRequestStatus.DENIED ? (
    <>
      <Button
        variant="outlined"
        sx={{
          fontWeight: 600,
          fontSize: '14px',
          lineHeight: '20px',
          color: '#ffffff',
          '&:hover': {
            borderColor: '#ffffff',
          },
        }}
        onClick={() => {
          setServiceTierChangeRequestDialogOpen(true);
        }}
      >
        REQUEST SERVICE TIER CHANGE
      </Button>
      {!isNil(terminalDisplayName) && !isNil(terminalId) ? (
        <>
          <ServiceTierChangeRequestDialog
            open={serviceTierChangeRequestDialogOpen}
            currentClinAndServiceTier={contractNumber}
            onDialogCancel={() => {
              setServiceTierChangeRequestDialogOpen(false);
            }}
            onDialogYes={() => {
              setServiceTierChangeRequestDialogOpen(false);
            }}
            terminalDisplayName={terminalDisplayName}
            terminalId={terminalId}
            formRef={formRef}
          />
          <ServiceTierChangeRequestErrorDialog
            open={!isNil(postServiceTierRequestError) ? true : false}
            onDialogCancel={() => {
              dispatch(clearReceivedPostServiceTierRequestStatus());
            }}
            formRef={formRef}
          />
        </>
      ) : (
        <></>
      )}
    </>
  ) : (
    <>
      <Button
        variant="outlined"
        sx={{
          fontWeight: 600,
          fontSize: '14px',
          lineHeight: '20px',
          color: '#ffffff',
          '&:hover': {
            borderColor: '#ffffff',
          },
        }}
        onClick={() => {
          setCancelServiceTierChangeRequestDialogOpen(true);
        }}
      >
        CANCEL SERVICE TIER CHANGE REQUEST
      </Button>
      <ModalDialog
        open={cancelServiceTierChangeRequestDialogOpen}
        message="Are you sure you want to cancel your Service Tier Change Request?"
        onRightBtnClicked={() => {
          setCancelServiceTierChangeRequestDialogOpen(false);
          dispatch(deleteServiceTierChangeRequest(currentServiceTierChangeRequest.id));
        }}
        onLeftBtnClicked={() => {
          setCancelServiceTierChangeRequestDialogOpen(false);
        }}
      />
      {!isNil(terminalDisplayName) && !isNil(terminalId) && (
        <ServiceTierChangeRequestSuccessDialog
          open={postServiceTierRequestSuccess ? true : false}
          onDialogCancel={() => {
            dispatch(clearReceivedPostServiceTierRequestStatus());
          }}
        />
      )}
    </>
  );
};
export default ServiceTierChangeRequestButton;
